import React from 'react';

import styled from 'styled-components';

import { PageHeaderContainerProps, PageHeaderProps } from './PageHeader.decl';

import { Typography, Box, useTheme, Spacer, Icon, ChevronLeftOutlined } from '@aircall/tractor';
import { RouterLink } from '@components/Link';
import { RouterLinkProps } from '@components/Link/Link.decl';
import { Tab } from '@components/Tab';
import { UserAvatar } from '@components/UserAvatar/UserAvatar';
import noop from 'lodash-es/noop';

const PageHeaderContainer = styled(Box)<PageHeaderContainerProps>`
  border-bottom: ${({ hasTabs }) => (hasTabs ? '0px' : '1px')} solid
    ${({ theme }) => theme.colors.grey.base};
`;

const TabMenu = styled(Box)`
  margin-top: -20px;
`;

export function PageHeaderLink({ children, to, ...linkProps }: RouterLinkProps) {
  return (
    <RouterLink to={to} color='neutral-1000' {...linkProps}>
      <Icon component={ChevronLeftOutlined} size={16} color='base.black' />
      {children}
    </RouterLink>
  );
}

const PageHeaderLinkStyled = styled(PageHeaderLink)`
  width: max-content;
  margin-top: -8px;
  margin-bottom: ${({ theme }) => theme.space.m}px;
`;

export function PageHeader({
  largeTitle,
  title,
  subtitle,
  'data-test': dataTest = 'page-header',
  icon,
  avatarSrc,
  avatarInitials = '',
  children,
  activeTabId = '1',
  gobackLinkRoute,
  gobackLinkText,
  onTabChange = noop,
  renderAvatar,
  renderExtra,
  renderSubLeft,
  renderSubRight,
  renderTitleRight,
  titleProps,
}: PageHeaderProps) {
  const { colors, space } = useTheme();
  const hasLargeTitle = !!largeTitle;
  const hasAvatar = !!(avatarSrc || avatarInitials);
  const hasGoBackLink = !!(gobackLinkRoute && gobackLinkText);
  const childrenArray = React.Children.toArray(children) as JSX.Element[];

  // Doing everything in one loop instead of using multiple filters
  const [tabMenu, tabContent, otherContent] = childrenArray.reduce(
    (acc, curr) => {
      switch (curr.type) {
        case Tab.Menu:
          acc[0].push(curr);
          break;
        case Tab.Content:
          acc[1].push(curr);
          break;
        default:
          acc[2].push(curr);
          break;
      }
      return acc;
    },
    [[], [], []] as Array<Array<JSX.Element>>
  );
  const hasTabs = !!tabMenu.length;

  const content = (
    <PageHeaderContainer backgroundColor={colors.base.white} data-test={dataTest} hasTabs={hasTabs}>
      <Box px='xl' pt={hasLargeTitle ? 'xl' : 'm'} pb='m'>
        {hasGoBackLink && (
          <PageHeaderLinkStyled data-test='page-header-link' to={gobackLinkRoute}>
            {gobackLinkText}
          </PageHeaderLinkStyled>
        )}
        <Spacer space='s' alignItems='center' width='100%' {...titleProps}>
          {renderAvatar?.()}
          {
            /* istanbul ignore next */
            !!icon && <Icon data-test='page-header-icon' component={icon} size={space.l} />
          }
          {hasAvatar && (
            <UserAvatar
              data-test='page-header-avatar'
              src={avatarSrc}
              initials={avatarInitials}
              size='medium'
            />
          )}
          <Spacer direction='vertical'>
            {hasLargeTitle && (
              <Typography variant='displayM' fontWeight={700} color='text.base'>
                {largeTitle}
              </Typography>
            )}
            {!!title && (
              <Typography variant='displayS' color='text.base'>
                {title}
              </Typography>
            )}
            {!!(renderSubLeft || subtitle || renderSubRight) && (
              <Spacer space='xxs' alignItems='center'>
                {renderSubLeft?.()}
                {!!subtitle && (
                  <Typography variant='body2' color='text.base'>
                    {subtitle}
                  </Typography>
                )}
                {renderSubRight?.()}
              </Spacer>
            )}
          </Spacer>
          {renderTitleRight?.()}
        </Spacer>
        {renderExtra?.()}
      </Box>
      {hasTabs && <TabMenu>{tabMenu}</TabMenu>}
    </PageHeaderContainer>
  );

  return hasTabs ? (
    <Tab.Container activeTabId={activeTabId} onChange={onTabChange}>
      {content}
      {otherContent}
      {tabContent}
    </Tab.Container>
  ) : (
    content
  );
}
